import { classNames } from '@/utils/class-names';
import React, { forwardRef, ReactNode } from 'react';
import LoadingSpin from './LoadingSpin';
import { Link } from 'react-router-dom';

import './index.css';

type ButtonProps = {
  children: ReactNode;
  as?: 'button' | 'a';
  onClick?: (e?: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  theme?: 'outline' | 'solid' | 'solid-white' | 'solid-primary-600';
  size?: 'default' | 'lg';
  className?: string;
  type?: 'submit' | 'button';
  loading?: boolean;
  id?: string;
};

type ButtonElementProps = ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorElementProps = ButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;

type ButtonLinkProps = ButtonElementProps | AnchorElementProps;

const getTheme = (theme: 'outline' | 'solid' | 'solid-white' | 'solid-primary-600' = 'solid') => {
  const defaultCss = 'font-semibold font-cadiz text-sh-16-24';

  const disableOutlineCss = 'disabled:bg-[#ffffff] disabled:border-[#BDBDBD] disabled:cursor-not-allowed';

  const disableCss =
    'disabled:bg-sh-grey-eee disabled:border-sh-grey-eee disabled:cursor-not-allowed disabled:opacity-50';

  switch (theme) {
    case 'outline':
      return `border-[1px] border-primary-600 bg-white hover:border-primary-600 selected:border-primary-600 focus:border-primary-600 focus:border-[2px] text-black-100 hover:text-primary-950 focus:text-primary-950 hover:bg-[#FFFFFF] sh-button-outline ${disableOutlineCss}`;
    case 'solid':
      return `selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-primary-600 text-secondary-700 disabled:text-black sh-button-solid ${defaultCss} ${disableCss}`;
    case 'solid-white':
      return `border-2 border-[#A1C3EC] hover:border-[#6AAEFE] selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-white text-primary-600 sh-button-solid ${defaultCss} ${disableCss}`;
    case 'solid-primary-600':
      return `selected:border-[#6AAEFE] focus:border-[#6AAEFE] bg-primary-600 text-secondary-700 disabled:text-black sh-button-solid ${defaultCss} ${disableCss}`;
    default:
      return ``;
  }
};

const getSize = (size: 'default' | 'lg') => {
  if (size === 'lg') {
    return `rounded-2xl py-5 px-sh-30`;
  }

  return `rounded-[8px] py-3 px-5`;
};

const CustomButton: React.ForwardRefRenderFunction<ButtonLinkProps, ButtonProps> = (
  {
    className = '',
    children,
    onClick,
    type = 'button',
    disabled,
    as,
    // size = 'md',
    theme = 'solid',
    loading,
    href,
    size = 'default',
    ...rest
  },
  ref,
) => {
  const Element = as === 'a' ? Link : 'button';
  const linkProps = as === 'a' ? { to: href } : {};

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      {...linkProps}
      {...rest}
      className={classNames(`sh-button`, getTheme(theme), getSize(size), className)}>
      <div className="sh-button flex items-center gap-2 ">
        {loading && <LoadingSpin />}
        {children}
      </div>
    </Element>
  );
};

export default forwardRef(CustomButton);
